import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TbPhotoPlus } from 'react-icons/tb';
import ReactCrop, {
  type Crop,
  makeAspectCrop,
  centerCrop,
} from 'react-image-crop';
import { blobToWebP } from 'webp-converter-browser';
import 'react-image-crop/dist/ReactCrop.css';
import { isNil } from 'lodash-es';
import { MdCancel } from 'react-icons/md';
import { MIME_IMAGE_TYPES } from '../../constants/Constants';
import Spinner from '../layout/Spinner/Spinner';

interface ImageUploadProps {
  onCropFinished: (value: HTMLImageElement) => void;
  onImageLoaded: (value: HTMLImageElement) => void;
  onValidSetup: (value: boolean) => void;
  triggerCrop: boolean;
  selectedImage?: HTMLImageElement;
}

const DEFAULT_MIN_IMAGE_SIZE = 512;
const DEFAULT_WEBP_LOSSY_RATIO = 0.9;

export const base64StringToBlob = (input: string): Blob => {
  const regexp = /data:(.*);base64,(.*)/g;
  const matches = [...input.matchAll(regexp)];

  const mimeType = matches[0][1];
  const base64Image = matches[0][2];

  const byteCharacters = atob(base64Image);
  const byteArrays = [];

  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }

  const byteArray = new Uint8Array(byteArrays);
  const inputBlob = new Blob([byteArray], { type: mimeType });

  return inputBlob;
};

const calculateDefaultCrop = (img: HTMLImageElement): Crop => {
  const imageRealWidth = img.naturalWidth;
  const imageRealHeight = img.naturalHeight;

  const minSize = Math.min(imageRealWidth, imageRealHeight);

  let c = null;
  if (imageRealWidth < imageRealHeight) {
    c = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: minSize,
        },
        1,
        imageRealWidth,
        imageRealHeight,
      ),
      imageRealWidth,
      imageRealHeight,
    );
  } else {
    c = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          height: minSize,
        },
        1,
        imageRealWidth,
        imageRealHeight,
      ),
      imageRealWidth,
      imageRealHeight,
    );
  }

  return c;
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  onCropFinished,
  triggerCrop,
  selectedImage,
  onImageLoaded,
  onValidSetup,
}) => {
  const { t } = useTranslation();
  const [selectedImg, setSelectedImg] = useState<HTMLImageElement | undefined>(
    selectedImage,
  );
  const [crop, setCrop] = useState<Crop | undefined>();
  const [isCropping, setIsCropping] = useState<boolean>(false);
  const [imageRatio, setImageRatio] = useState<number>(1.0);
  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(false);

  const minSizes = useMemo<number[]>(() => {
    const minWidth = DEFAULT_MIN_IMAGE_SIZE / imageRatio;
    const minHeight = DEFAULT_MIN_IMAGE_SIZE / imageRatio;

    return [minWidth, minHeight];
  }, [imageRatio]);

  const shouldCrop = useMemo(() => triggerCrop, [triggerCrop]);

  useEffect(() => {
    onValidSetup(false);
  }, [onValidSetup]);

  const invalidCrop = useMemo<boolean>(() => {
    if (!selectedImage) {
      return false;
    }

    if (!crop) {
      return true;
    }

    if (crop.width <= 0 || crop.height <= 0) {
      return true;
    }

    return false;
  }, [crop, selectedImage]);

  useEffect(() => {
    if (invalidCrop && selectedImage) {
      const c = calculateDefaultCrop(selectedImage);
      setCrop(c);
    }
  }, [invalidCrop]);

  useEffect(() => {
    if (shouldCrop && !isCropping && selectedImage && crop) {
      setIsCropping(true);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        return;
      }

      const imageRealWidth = selectedImage.naturalWidth;
      const imageRealHeight = selectedImage.naturalHeight;

      const cropUnitPx = crop.unit === 'px';
      const cropX = cropUnitPx
        ? Math.round(crop.x * imageRatio)
        : Math.round(crop.x * 0.01 * imageRealWidth);
      const cropY = cropUnitPx
        ? Math.round(crop.y * imageRatio)
        : Math.round(crop.y * 0.01 * imageRealHeight);
      const cropWidth = cropUnitPx
        ? Math.round(crop.width * imageRatio)
        : Math.round(crop.width * 0.01 * imageRealWidth);
      const cropHeight = cropUnitPx
        ? Math.round(crop.height * imageRatio)
        : Math.round(crop.height * 0.01 * imageRealHeight);

      canvas.width = DEFAULT_MIN_IMAGE_SIZE;
      canvas.height = DEFAULT_MIN_IMAGE_SIZE;
      context.drawImage(
        selectedImage,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        DEFAULT_MIN_IMAGE_SIZE,
        DEFAULT_MIN_IMAGE_SIZE,
      );
      const croppedData = canvas.toDataURL();

      const croppedImageReader = new FileReader();
      croppedImageReader.onload = async (event) => {
        if (event?.target?.result) {
          let fileSelected = null;
          if (event.target.result.constructor === ArrayBuffer) {
            fileSelected = new Uint8Array(event.target.result).toString();
          } else if (event.target.result.constructor === String) {
            fileSelected = event.target.result;
          }

          if (fileSelected) {
            const img = new Image();
            img.src = fileSelected;

            img.onload = () => {
              onCropFinished(img);
            };
          }
        }
      };

      const croppedBlob = base64StringToBlob(croppedData);

      blobToWebP(croppedBlob, {
        quality: DEFAULT_WEBP_LOSSY_RATIO,
      })
        .then((croppedWebpBlob: Blob) => {
          croppedImageReader.readAsDataURL(croppedWebpBlob);
        })
        .catch((reason) => {
          console.error(reason);
        });
    }
  }, [shouldCrop]);

  const onImgLoad = (img: HTMLImageElement) => {
    const imageRealWidth = img.naturalWidth;
    const imageRealHeight = img.naturalHeight;

    const minSize = Math.min(imageRealWidth, imageRealHeight);

    if (minSize < DEFAULT_MIN_IMAGE_SIZE) {
      setSelectedImg(undefined);
      setIsLoadingImage(false);
      toast.error(t('static.join.modal_step_photo_notification_image_small'));
      return;
    }

    const imageScreenWidth = img.offsetWidth;

    const widthRatio = 0.0 + imageRealWidth / imageScreenWidth;

    const c = calculateDefaultCrop(img);

    setCrop(c);
    onValidSetup(true);
    setImageRatio(widthRatio);
    onImageLoaded(img);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length <= 0) {
      console.error('acceptedFiles length is zero');
      return;
    }

    const file = acceptedFiles[0];

    const webpReader = new FileReader();
    webpReader.onload = async (event) => {
      if (event?.target?.result) {
        let fileSelected = null;
        if (event.target.result.constructor === ArrayBuffer) {
          fileSelected = new Uint8Array(event.target.result).toString();
        } else if (event.target.result.constructor === String) {
          fileSelected = event.target.result;
        }

        if (fileSelected) {
          const img = new Image();
          img.src = fileSelected;

          img.onload = () => {
            setIsLoadingImage(false);
            setSelectedImg(img);
          };
        }
      }
    };

    const fileReader = new FileReader();
    fileReader.onload = async (event) => {
      if (event?.target?.result) {
        let fileSelected = null;
        if (event.target.result.constructor === ArrayBuffer) {
          fileSelected = new Uint8Array(event.target.result).toString();
        } else if (event.target.result.constructor === String) {
          fileSelected = event.target.result;
        }

        if (fileSelected) {
          const inputBlob = base64StringToBlob(fileSelected);
          webpReader.readAsDataURL(inputBlob);
        }
      }
    };

    setIsLoadingImage(true);
    fileReader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  const onRemoveImage = useCallback(() => {
    setSelectedImg(undefined);
    onValidSetup(false);
  }, [onValidSetup]);

  useEffect(() => {
    if (isNil(selectedImage) && !isNil(crop)) {
      onRemoveImage();
    }
  }, [selectedImage, crop, onRemoveImage]);

  return (
    <div className="flex flex-col grow items-center justify-center min-w-full">
      {isLoadingImage ? (
        <div className="flex min-w-full flex-col items-center justify-center grow">
          <Spinner color="fill-sky-600" />
        </div>
      ) : (
        !selectedImg && (
          <div
            className="flex min-w-full cursor-pointer flex-col items-center justify-center gap-4 border-2 border-dashed border-neutral-300 p-20 text-neutral-600 transition hover:opacity-70"
            {...getRootProps()}
          >
            <input
              aria-label={t('static.join.modal_step_photo_droplet_label')}
              data-test-id="image-upload-input"
              type="file"
              name="image"
              accept={MIME_IMAGE_TYPES}
              {...getInputProps()}
            />
            <TbPhotoPlus size={50} />
            <div className="text-lg font-semibold">
              {t('static.join.modal_step_photo_droplet_label')}
            </div>
          </div>
        )
      )}
      {selectedImg && (
        <div className="flex min-w-full flex-col items-center justify-center gap-0 grow">
          <span className="relative flex flex-col items-center justify-center min-w-full border-2 border-neutral-300 p-0 text-neutral-600 transition">
            <ReactCrop
              data-test-id="crop-img"
              crop={crop}
              aspect={1}
              minWidth={minSizes[0]}
              minHeight={minSizes[1]}
              onChange={(c) => setCrop(c)}
            >
              <img
                alt="Upload"
                onLoad={(e) => onImgLoad(e.target as HTMLImageElement)}
                style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                src={selectedImg.src}
              />
            </ReactCrop>
          </span>
          <span className="absolute right-[-12px] top-[-12px] flex min-w-full flex-row items-end justify-end text-end">
            <button
              data-test-id="remove-profile-image"
              id="remove-profile-image"
              key="remove-profile-image"
              aria-label="remove-profile-image"
              className="rounded-full bg-white hover:text-sky-600 hover:shadow-lg"
              type="button"
              onClick={onRemoveImage}
            >
              <MdCancel size={32} />
            </button>
          </span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
